import type {
  PaymentRequest,
  StripeCardNumberElement,
} from "@stripe/stripe-js";

import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  PaymentRequestButtonElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { Pressable, StyleSheet, TextInput, View } from "react-native";

import { sessionAtom } from "@/atoms/session";
import { GText } from "@/components/g-text";
import { colors } from "@/constants/colors";
import { supabase } from "@/lib/supabase";

const cardElementOptions = {
  style: {
    base: {
      "::placeholder": {
        color: colors.subtle,
      },
      color: colors.white,
      fontFamily: "inherit",
      fontSize: "16px",
    },
    invalid: {
      color: colors.red,
    },
  },
};

export default function CreditCardEntry({
  clientSecret,
  onClose,
  paymentRequest,
  price,
}: {
  clientSecret: string;
  onClose: (success: boolean) => void;
  paymentRequest: null | PaymentRequest;
  price: string;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const sessionState = useAtomValue(sessionAtom);
  const session = sessionState === "loading" ? null : sessionState;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [name, setName] = useState("");
  const [nameNeeded, setNameNeeded] = useState(false);

  useEffect(() => {
    if (!session?.user.id) return;

    supabase
      .from("profile")
      .select("name")
      .eq("user_id", session.user.id)
      .single()
      .then(({ data }) => {
        if (!data?.name) {
          setNameNeeded(true);
        }
      });
  }, [session?.user.id]);

  useEffect(() => {
    if (!stripe || !paymentRequest) return;

    paymentRequest.on("paymentmethod", async (ev) => {
      const { error: confirmError, paymentIntent } =
        await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false },
        );

      if (confirmError) {
        ev.complete("fail");
        setError(confirmError.message ?? "Payment failed");
      } else if (paymentIntent?.status === "requires_action") {
        const { error: actionError } =
          await stripe.confirmCardPayment(clientSecret);
        if (actionError) {
          ev.complete("fail");
          setError(actionError.message ?? "Payment failed");
        } else {
          ev.complete("success");
          onClose(true);
        }
      } else {
        ev.complete("success");
        onClose(true);
      }
    });
  }, [stripe, clientSecret, onClose, paymentRequest]);

  async function handleSubmit() {
    if (!stripe || !elements) return;

    const cardNumber = elements.getElement(
      CardNumberElement,
    ) as StripeCardNumberElement;
    if (!cardNumber) return;

    setSubmitting(true);
    setError(null);

    const billingName = name.trim();

    const { error: confirmError } = await stripe.confirmCardPayment(
      clientSecret,
      {
        payment_method: {
          billing_details: billingName ? { name: billingName } : undefined,
          card: cardNumber,
        },
      },
    );

    setSubmitting(false);

    if (confirmError) {
      setError(confirmError.message ?? "Payment failed");
    } else {
      if (billingName && session?.user.id) {
        supabase
          .from("profile")
          .update({ name: billingName.toLowerCase() })
          .eq("user_id", session.user.id)
          .then();
      }
      onClose(true);
    }
  }

  const isSubmitDisabled =
    submitting || !stripe || !elements || (nameNeeded && !name.trim());
  return (
    <View>
      <View style={styles.header}>
        <GText style={styles.modalHeading}>enter payment details</GText>
      </View>

      {paymentRequest && (
        <>
          <View style={styles.walletWrapper}>
            <PaymentRequestButtonElement
              options={{
                paymentRequest,
                style: {
                  paymentRequestButton: {
                    height: "48px",
                    theme: "light",
                    type: "default",
                  },
                },
              }}
            />
          </View>
          <View style={styles.divider}>
            <View style={styles.dividerLine} />
            <GText style={styles.dividerText}>or pay with card</GText>
            <View style={styles.dividerLine} />
          </View>
        </>
      )}

      <form
        autoComplete="on"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        {nameNeeded && (
          <View style={styles.fieldContainer}>
            <TextInput
              autoCapitalize="words"
              autoComplete="name"
              onChangeText={setName}
              placeholder="full name"
              placeholderTextColor={colors.subtle}
              style={styles.nameInput}
              value={name}
            />
          </View>
        )}

        <View style={styles.fieldContainer}>
          <View style={styles.input}>
            <CardNumberElement
              options={{ ...cardElementOptions, placeholder: "card number" }}
            />
          </View>
        </View>

        <View style={styles.row}>
          <View style={styles.flexOne}>
            <View style={styles.input}>
              <CardExpiryElement
                options={{ ...cardElementOptions, placeholder: "MM / YY" }}
              />
            </View>
          </View>
          <View style={styles.flexOne}>
            <View style={styles.input}>
              <CardCvcElement
                options={{ ...cardElementOptions, placeholder: "CVC" }}
              />
            </View>
          </View>
        </View>

        {error && <GText style={styles.paymentErrorText}>{error}</GText>}
        <Pressable
          disabled={isSubmitDisabled}
          onPress={handleSubmit}
          style={[styles.payBtn, isSubmitDisabled && styles.btnDisabled]}
        >
          <GText style={styles.payBtnText}>
            {submitting ? "Processing..." : `Pay ${price}`}
          </GText>
        </Pressable>
      </form>
    </View>
  );
}

const styles = StyleSheet.create({
  btnDisabled: {
    opacity: 0.5,
  },
  divider: {
    alignItems: "center",
    flexDirection: "row",
    marginVertical: 16,
  },
  dividerLine: {
    backgroundColor: colors.darkGray,
    flex: 1,
    height: 1,
  },
  dividerText: {
    color: colors.subtle,
    fontSize: 13,
    paddingHorizontal: 12,
  },
  fieldContainer: {
    marginBottom: 12,
  },
  flexOne: {
    flex: 1,
  },
  header: {
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  },
  input: {
    backgroundColor: colors.cardBg,
    borderColor: colors.midGray,
    borderRadius: 9999,
    borderWidth: 1,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  modalHeading: {
    color: colors.light,
    fontSize: 24,
    fontWeight: "bold",
  },
  nameInput: {
    backgroundColor: colors.cardBg,
    borderColor: colors.midGray,
    borderRadius: 9999,
    borderWidth: 1,
    color: colors.white,
    fontSize: 16,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  payBtn: {
    alignItems: "center",
    backgroundColor: colors.white,
    borderRadius: 28,
    marginTop: 16,
    paddingVertical: 14,
    width: "100%",
  },
  payBtnText: {
    color: colors.black,
    fontSize: 16,
    fontWeight: "600",
  },
  paymentErrorText: {
    color: colors.red,
    marginTop: 8,
  },
  row: {
    flexDirection: "row",
    gap: 12,
  },
  walletWrapper: {
    borderRadius: 9999,
    overflow: "hidden",
  },
});
